/**
 * IN-PLACE PARTITION EDITS — one of the app's TWO structural edit engines.
 *
 * WHICH ENGINE DO I WANT?
 * -----------------------
 * `services/boundaryEdits.ts` (this file) — IN-PLACE. It mutates the existing
 *   rows: the edited clip keeps its id, its neighbour is resized, a silence
 *   tile is minted when the freed audio has nowhere to go, and everything is
 *   renumbered by `startSec`. Nothing is ever marked obsolete, so there is no
 *   audit trail. Used by the WaveTrimmer on the Correct tab.
 *
 * `services/recut.ts` (+ `cutOps.ts`) — VERSIONED. Inputs become obsolete
 *   (stamps kept, cached audio dropped) and fresh records are minted with new
 *   names (A1.7, A1.8…) and `madeFrom` links back to the inputs. Used by the
 *   Cuts station.
 *
 * They are NOT interchangeable and must not be merged: the versioned engine
 * cannot express "grow this clip by 0.5 s and let the neighbour absorb the
 * loss" without minting two new records for a nudge, and the in-place engine
 * cannot express "this word moved" at all. Two stations, two semantics.
 *
 * THE PARTITION INVARIANT — every millisecond of a recording is labeled
 * exactly once. FOUR places in the app keep it, and they do two different
 * jobs:
 *   CHECK    `recut.assertTiles`          — throws when a recut does not tile
 *            `WaveLane.validateMoved`     — refuses an illegal drag
 *   CONSTRUCT `pipeline/alignedTiling.tileFromWords` — seals the seams as it
 *                                           builds the timeline
 *            `boundaryEdits.moveBoundary` — moving an edge TRANSFERS audio, so
 *                                           nothing is ever left unlabeled
 *
 * THE RULE BOTH ENGINES OBEY: the segments of a task are a PARTITION of its audio —
 * every millisecond belongs to exactly one tile, always. So no operation ever
 * "just moves an edge": moving an edge is a TRANSFER of audio between two
 * adjacent tiles. Growing takes from the neighbour; shrinking gives to the
 * neighbour (or mints a silence tile when the neighbour is human-owned and must
 * not be touched). Nothing is left unlabeled, ever — a blank stretch is a bug.
 *
 * ATOMICITY: every write of one operation lands in ONE `reviseSegments`
 * transaction, so a failure can never leave two segments claiming one index or
 * a hole in the timeline.
 */

import { getSegment, getSegmentsByTask, newId, reconcileTask, reviseSegments, type SegmentRevision } from '../db';
import { clamp, round3 } from '../lib/num';
import { activeLeaves, type Segment, type TaskStatus } from '../types';
import { sliceToWav } from './audioSlicer';
import { decodedAudioFor } from './decodedAudio';

/** The shared single-slot decode, with this engine's own failure wording. */
const bufferFor = (taskId: string): Promise<AudioBuffer> =>
  decodedAudioFor(taskId, 'The original audio for this task is missing, so the clip cannot be re-cut.');

/** How far one press of an extend/shrink button reaches. */
export const EXTEND_STEP_SEC = 0.5;
/** A real (word/filler/noise) neighbour may never shrink below this. */
export const NEIGHBOUR_CORE_SEC = 0.15;
/** The edited tile itself may never shrink below this. */
const SELF_MIN_SEC = 0.12;
/** A machine gap tile (silence/unclear) smaller than this is absorbed whole. */
const ABSORB_BELOW_SEC = 0.08;

export interface BoundaryOutcome {
  segment: Segment;
  /** Every OTHER row that changed (resized neighbours, renumbered rows). */
  changed: Segment[];
  /** Silence tiles minted to keep the partition complete. */
  created: Segment[];
  /** Rows absorbed whole. */
  deletedIds: string[];
  /** Seconds the edited boundary actually moved (0 = wall). */
  movedSec: number;
  /** Why the press did less than asked, phrased for the UI. */
  note?: string;
  status?: TaskStatus;
}

/**
 * Move ONE boundary of a segment by `deltaSec` (negative = inward/shrink),
 * keeping the task a perfect partition. All writes land in one transaction.
 */
async function moveBoundary(
  segmentId: string,
  side: 'start' | 'end',
  deltaSec: number,
): Promise<BoundaryOutcome> {
  const seg = await getSegment(segmentId);
  if (!seg) throw new Error('That segment is no longer there.');
  const siblings = activeLeaves(await getSegmentsByTask(seg.taskId));
  const pos = siblings.findIndex((s) => s.id === seg.id);
  if (pos < 0) throw new Error('That segment is no longer part of its task.');
  const buffer = await bufferFor(seg.taskId);
  const durationSec = buffer.duration;

  const growing = (side === 'start' && deltaSec < 0) || (side === 'end' && deltaSec > 0);
  const neighbour = side === 'start' ? siblings[pos - 1] : siblings[pos + 1];
  const oldEdge = side === 'start' ? seg.startSec : seg.endSec;
  let target = clamp(oldEdge + deltaSec, 0, durationSec);
  let note: string | undefined;

  const changed: Segment[] = [];
  const created: Segment[] = [];
  const deletedIds: string[] = [];

  if (!growing) {
    /* -- SHRINK: give the freed audio away, never leave it blank ---- */
    const selfFloor =
      side === 'start' ? seg.endSec - SELF_MIN_SEC : seg.startSec + SELF_MIN_SEC;
    if (side === 'start' ? target > selfFloor : target < selfFloor) {
      target = selfFloor;
      note = 'the clip is at its minimum size';
    }
    const freedFrom = side === 'start' ? oldEdge : target;
    const freedTo = side === 'start' ? target : oldEdge;
    const freed = freedTo - freedFrom;
    if (freed > 0.001) {
      const humanOwned = neighbour && (neighbour.placedBy === 'human' || neighbour.verify !== 'pending');
      if (neighbour && !humanOwned) {
        // The neighbour absorbs the freed audio — filler swallows its lost
        // tail, silence widens; either way the timeline stays fully labeled.
        changed.push(
          side === 'start' ? { ...neighbour, endSec: target } : { ...neighbour, startSec: target },
        );
      } else if (freed >= ABSORB_BELOW_SEC) {
        // Human-owned neighbour (or file edge): mint a silence tile instead of
        // touching ground truth. It joins the Verify queue like everything else.
        created.push({
          id: newId(),
          taskId: seg.taskId,
          index: -1, // renumbered below
          startSec: round3(freedFrom),
          endSec: round3(freedTo),
          heard: 'silence',
          contextual: 'freed by a manual resize — confirm what is here',
          confidence: 0.5,
          kind: 'silence',
          verify: 'pending',
        });
      } else {
        // Sliver too small to stand alone next to ground truth: don't shrink.
        target = oldEdge;
        note = 'too small a step next to an already-judged clip';
      }
    }
  } else if (neighbour) {
    /* -- GROW: take from the neighbour, by its rules ----------------- */
    const humanOwned = neighbour.placedBy === 'human' || neighbour.verify !== 'pending';
    const isGapTile = neighbour.kind === 'silence' || neighbour.kind === 'unclear';
    const nearEdge = side === 'start' ? neighbour.endSec : neighbour.startSec;
    const farEdge = side === 'start' ? neighbour.startSec : neighbour.endSec;
    const crossing = side === 'start' ? target < nearEdge : target > nearEdge;

    if (crossing) {
      if (humanOwned) {
        target = nearEdge;
        note = `stopped at “${neighbour.heard}” — a human already touched it`;
      } else if (isGapTile) {
        const remaining = side === 'start' ? target - farEdge : farEdge - target;
        if (remaining < ABSORB_BELOW_SEC) {
          target = farEdge;
          deletedIds.push(neighbour.id);
        } else {
          changed.push(
            side === 'start' ? { ...neighbour, endSec: target } : { ...neighbour, startSec: target },
          );
        }
      } else {
        const coreEdge =
          side === 'start'
            ? Math.min(nearEdge, neighbour.startSec + NEIGHBOUR_CORE_SEC)
            : Math.max(nearEdge, neighbour.endSec - NEIGHBOUR_CORE_SEC);
        const clampedTarget = side === 'start' ? Math.max(target, coreEdge) : Math.min(target, coreEdge);
        if (clampedTarget !== target) {
          note = `reached “${neighbour.heard}” — fix that tile instead if it is wrong`;
          target = clampedTarget;
        }
        if (target !== nearEdge) {
          changed.push(
            side === 'start' ? { ...neighbour, endSec: target } : { ...neighbour, startSec: target },
          );
        }
      }
    }
  }

  const movedSec = Math.abs(target - oldEdge);
  if (movedSec <= 0.001) {
    return { segment: seg, changed: [], created: [], deletedIds: [], movedSec: 0, note: note ?? 'nothing to change there' };
  }

  const updated: Segment = {
    ...seg,
    startSec: side === 'start' ? round3(target) : seg.startSec,
    endSec: side === 'end' ? round3(target) : seg.endSec,
    confidence: 1,
    placedBy: 'human',
  };

  /* -- renumber by time and persist everything atomically ------------ */
  const byId = new Map<string, Segment>();
  for (const s of siblings) byId.set(s.id, s);
  byId.set(updated.id, updated);
  for (const c of changed) byId.set(c.id, c);
  for (const c of created) byId.set(c.id, c);
  for (const d of deletedIds) byId.delete(d);
  const ordered = [...byId.values()].sort((a, b) => a.startSec - b.startSec);
  const revisions: SegmentRevision[] = [];
  const touched = new Set<string>([updated.id, ...changed.map((c) => c.id), ...created.map((c) => c.id)]);
  ordered.forEach((s, i) => {
    const reindexed = s.index !== i;
    if (reindexed) s.index = i;
    if (touched.has(s.id)) {
      revisions.push({ segment: s, clip: sliceToWav(buffer, s.startSec, s.endSec) });
    } else if (reindexed) {
      revisions.push({ segment: s });
    }
  });
  await reviseSegments(revisions, deletedIds);
  const status = await reconcileTask(seg.taskId);

  return { segment: updated, changed, created, deletedIds, movedSec, note, status };
}

/**
 * Set both boundaries at once (the trimmer's save path). Runs as two
 * `moveBoundary` steps so every displaced millisecond is re-homed by the same
 * rules — the bug this replaces was the trimmer writing one row and leaving
 * the freed audio labeled as nothing at all.
 */
export async function setBoundaries(
  segmentId: string,
  startSec: number,
  endSec: number,
): Promise<BoundaryOutcome> {
  const seg = await getSegment(segmentId);
  if (!seg) throw new Error('That segment is no longer there.');
  let out: BoundaryOutcome | null = null;
  if (Math.abs(startSec - seg.startSec) > 0.001) {
    out = await moveBoundary(segmentId, 'start', startSec - seg.startSec);
  }
  if (Math.abs(endSec - seg.endSec) > 0.001) {
    const mid = await getSegment(segmentId);
    if (mid) out = await moveBoundary(segmentId, 'end', endSec - mid.endSec);
  }
  if (!out) {
    return { segment: seg, changed: [], created: [], deletedIds: [], movedSec: 0, note: 'no change' };
  }
  return out;
}
